/**
 * Agent Runs — persistent, patient-searchable history of AI scrape-agent runs.
 *
 * A toolbar button (with a live active-count badge) on the Insurance
 * Verification page opens a right-hand drawer listing every run for the office:
 * active ones pinned on top (live, via the WS-backed redux store), past ones
 * below (fetched from GET /clinics/api/scrape-jobs/). Typing filters by patient.
 *
 * Complements the header bell (ambient "what's happening now"); this is the
 * full ledger. Reuses the scrape-job row visual language from
 * ScrapeJobsIndicator.css. react-redux 6.x → connect(); UI state via hooks.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Drawer, Input, Tooltip, Empty, Spin, message } from "antd";
import {
  RobotOutlined,
  SearchOutlined,
  ReloadOutlined,
  LoadingOutlined,
  CheckOutlined,
  CloseOutlined,
  KeyOutlined,
  StopOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useDebounce } from "use-debounce";

import { apiService } from "../../_services/api.service";
import { controller as rootController } from "../../controller";
import {
  selectAllJobs,
  SCRAPE_JOB_UPSERT,
} from "../../reducers/scrapeJobs";
import TreatmentPlanListRowInsuranceActions from "../TreatmentPlans/component/TreatmentPlanListRowInsuranceActions";
import "./ScrapeJobsIndicator.css";
import "./AgentRuns.css";

const ACTIVE_STATES = new Set(["queued", "claimed", "running", "awaiting_2fa"]);

// Breakdown/eligibility runs land on the breakdown tab; predeterminations on
// the estimates tab.
function tabForOperation(op) {
  return op === "predetermination" ? "estimates" : "breakdown";
}

/* --- presentation helpers (mirror ScrapeJobsNotificationButton) --- */
function carrierName(job) {
  if (!job || !job.carrier_code) return "";
  const c = String(job.carrier_code).toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}
function jobLabel(job) {
  const op = (job.operation_type || "").replace(/_/g, " ");
  if (!op) return "Insurance fetch";
  return op.charAt(0).toUpperCase() + op.slice(1);
}
function patientLabel(job) {
  return job.patient_name || (job.patient_id ? "Patient #" + job.patient_id : "Patient");
}
function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return diffSec + "s ago";
  if (diffSec < 3600) return Math.floor(diffSec / 60) + "m ago";
  if (diffSec < 86400) return Math.floor(diffSec / 3600) + "h ago";
  return Math.floor(diffSec / 86400) + "d ago";
}
function formatAbsolute(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}
function isActive(job) {
  return ACTIVE_STATES.has(job.state);
}
function isHidden(job) {
  return !!(job && job.hidden_at);
}
function variantFor(job) {
  if (job.state === "awaiting_2fa") return "2fa";
  if (ACTIVE_STATES.has(job.state)) return "running";
  if (job.state === "succeeded") return "success";
  if (job.state === "failed" || job.state === "timed_out") return "failed";
  return "cancelled";
}
function iconFor(job) {
  const v = variantFor(job);
  if (v === "running") return <LoadingOutlined spin />;
  if (v === "2fa") return <KeyOutlined />;
  if (v === "success") return <CheckOutlined />;
  if (v === "failed") return <CloseOutlined />;
  return <StopOutlined />;
}
function statusLine(job) {
  const op = jobLabel(job);
  const tail = carrierName(job) ? " · " + carrierName(job) : "";
  switch (job.state) {
    case "queued": return "Waiting in queue · " + op + tail;
    case "claimed": return "Starting · " + op + tail;
    case "running": return "Running · " + op + tail;
    case "awaiting_2fa": return "Needs 2FA code · " + op + tail;
    case "succeeded": return "Succeeded · " + op + tail;
    case "failed": return "Failed · " + op + tail;
    case "timed_out": return "Timed out · " + op + tail;
    case "cancelled": return "Cancelled · " + op + tail;
    default: return op + tail;
  }
}

function AgentRunsLauncher({ liveJobs, upsertJob }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 400);
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stateFilter, setStateFilter] = useState("all"); // all | active | succeeded | failed
  // Patient whose insurance modal is open, launched from a clicked run.
  const [insuranceTarget, setInsuranceTarget] = useState(null);
  // Hiding a run is a super-admin tool. Same checkPracticeAdmin call the other
  // admin surfaces use; the server gates the endpoint regardless of this flag.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    rootController.checkPracticeAdmin().then((res) => {
      if (cancelled) return;
      setIsSuperAdmin(Boolean(res && res.data && res.data.result === "super_admin"));
    }).catch(() => { /* not a super admin, or the check failed — no hide controls. */ });
    return () => { cancelled = true; };
  }, []);

  const fetchRuns = useCallback(async (q, opts) => {
    const silent = !!(opts && opts.silent);
    const wantHidden = !!(opts && opts.includeHidden);
    if (!silent) setLoading(true);
    try {
      const qp = q && q.trim() ? "&q=" + encodeURIComponent(q.trim()) : "";
      const officeId = localStorage.getItem("selectedOffice");
      const officeQp = officeId ? "&office_id=" + encodeURIComponent(officeId) : "";
      // Hidden runs are excluded server-side unless asked for, so the Hidden tab
      // is the only listing that carries them — and only for a super-admin.
      const hiddenQp = wantHidden ? "&include_hidden=1" : "";
      const data = await apiService.get(
        "/clinics/api/scrape-jobs/?limit=200" + qp + officeQp + hiddenQp);
      setFetched(data && Array.isArray(data.results) ? data.results : []);
    } catch (e) {
      // Background refreshes fail quietly — keep the last good list.
      if (!silent) {
        message.error("Couldn't load agent runs");
        setFetched([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // (Re)fetch history whenever the drawer opens or the (debounced) search changes.
  const showingHidden = stateFilter === "hidden";
  useEffect(() => {
    if (open) fetchRuns(debouncedSearch, { includeHidden: showingHidden });
  }, [open, debouncedSearch, showingHidden, fetchRuns]);

  // While the drawer is open, quietly reconcile against the server on an
  // interval. A completed run then flips to its terminal status even if its
  // live WS event was missed (e.g. the socket was mid-reconnect during a long
  // scrape) — no manual close/reopen needed.
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(
      () => fetchRuns(debouncedSearch, { silent: true, includeHidden: showingHidden }), 12000);
    return () => clearInterval(id);
  }, [open, debouncedSearch, showingHidden, fetchRuns]);

  // Merge fetched history with the live redux jobs: live state wins (so a
  // running row flips to "Succeeded" in place as WS events land), and a
  // just-started job not yet in the fetch is added. Then apply the client-side
  // search + state filter so redux-injected rows respect them too.
  const rows = useMemo(() => {
    const tsOf = (j) => {
      const t = new Date((j && (j.updated_at || j.completed_at || j.created_at)) || 0).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const byId = {};
    fetched.forEach((j) => { byId[j.job_id] = j; });
    // Live state usually wins (so a running row flips in place as WS events
    // land), BUT prefer whichever record was updated more recently — a freshly
    // fetched "succeeded" must not be clobbered by a stale live "running" left
    // over from a WS completion event that was dropped during a reconnect.
    (liveJobs || []).forEach((j) => {
      const prev = byId[j.job_id];
      byId[j.job_id] = !prev || tsOf(j) >= tsOf(prev) ? j : prev;
    });
    let list = Object.keys(byId).map((id) => byId[id]);

    // Scope to the header's selected office. The live WS/redux jobs are scoped to
    // the whole USER (all their offices), so filter client-side too — matching
    // the server's office_id filter so no other-office run leaks into the panel.
    const selectedOfficeId = localStorage.getItem("selectedOffice");
    if (selectedOfficeId) {
      list = list.filter((j) => j.office_id != null && String(j.office_id) === String(selectedOfficeId));
    }

    const term = (debouncedSearch || "").trim().toLowerCase();
    if (term) {
      const tokens = term.split(/\s+/);
      list = list.filter((j) => {
        const name = (j.patient_name || "").toLowerCase();
        return tokens.every((t) => name.indexOf(t) !== -1);
      });
    }
    // Hidden runs are their own tab, and are filtered out of every other one
    // HERE as well as server-side: the live redux copy of a run the super-admin
    // just hid has no idea it was hidden until its upsert lands, and a stale one
    // from earlier in the session would otherwise walk back into the list.
    if (stateFilter === "hidden") {
      list = list.filter(isHidden);
    } else {
      list = list.filter((j) => !isHidden(j));
      if (stateFilter === "active") list = list.filter(isActive);
      else if (stateFilter === "succeeded") list = list.filter((j) => j.state === "succeeded");
      else if (stateFilter === "failed") list = list.filter((j) => j.state === "failed" || j.state === "timed_out");
    }

    list.sort((a, b) => {
      const aa = isActive(a), ba = isActive(b);
      if (aa !== ba) return aa ? -1 : 1; // active pinned on top
      const at = new Date(a.created_at || 0).getTime();
      const bt = new Date(b.created_at || 0).getTime();
      return bt - at; // newest first
    });
    return list;
  }, [fetched, liveJobs, debouncedSearch, stateFilter]);

  const activeRows = rows.filter(isActive);
  const pastRows = rows.filter((j) => !isActive(j));

  // Clicking a run opens that patient's insurance modal (breakdown/estimates),
  // on the tab matching the run's operation. We reuse the row-actions component
  // headlessly; keying by patient id remounts it so a fresh click re-opens.
  const openInsuranceForJob = (job) => {
    if (!job || !job.patient_id) return;
    const parts = (job.patient_name || "").trim().split(/\s+/);
    setOpen(false);
    setInsuranceTarget({
      patient: {
        id: job.patient_id,
        first_name: parts[0] || "",
        last_name: parts.slice(1).join(" "),
      },
      tab: tabForOperation(job.operation_type),
    });
  };
  const handleCancel = async (job, e) => {
    e.stopPropagation();
    try {
      const data = await apiService.post("/clinics/api/scrape-jobs/" + job.job_id + "/cancel/", {});
      if (data && data.job_id) upsertJob(data);
      message.success("Cancelled");
    } catch (err) {
      message.error("Could not cancel");
    }
  };
  // Super-admin only. Hiding takes a finished run out of the panel; it does NOT
  // delete it — the run, its result and its error text are all still on the
  // server, and the Hidden tab brings it back. Upserting the response into redux
  // is what stops the live copy re-adding the row a moment later.
  const handleHide = async (job, e) => {
    e.stopPropagation();
    const hide = !isHidden(job);
    try {
      const data = await apiService.post(
        "/clinics/api/scrape-jobs/" + job.job_id + "/hide/", { hidden: hide });
      if (data && data.job_id) upsertJob(data);
      setFetched((prev) => prev.map((j) => (j.job_id === job.job_id ? { ...j, ...data } : j)));
      message.success(hide ? "Run hidden" : "Run restored");
    } catch (err) {
      message.error(hide ? "Could not hide this run" : "Could not restore this run");
    }
  };

  const openTwoFA = (job, e) => {
    e.stopPropagation();
    setOpen(false);
    window.dispatchEvent(new CustomEvent("scrapeJobs.openTwoFA", { detail: { jobId: job.job_id } }));
  };

  function renderRow(job) {
    const variant = variantFor(job);
    const cancellable = job.state === "queued" || job.state === "awaiting_2fa";
    const when = job.completed_at || job.updated_at || job.created_at;
    return (
      <div
        key={job.job_id}
        className={
          "scrape-popover-row scrape-popover-row-" + variant + " agent-runs-row"
          + (isHidden(job) ? " is-hidden" : "")
        }
        onClick={() => openInsuranceForJob(job)}
        role="button"
        tabIndex={0}
      >
        <div className="scrape-popover-row-icon">{iconFor(job)}</div>
        <div className="scrape-popover-row-body">
          <div className="scrape-popover-row-name">{patientLabel(job)}</div>
          <div className="scrape-popover-row-meta">{statusLine(job)}</div>
          {job.error_message && variant === "failed" ? (
            <span className="scrape-popover-row-error">{job.error_message}</span>
          ) : null}
        </div>
        <div className="scrape-popover-row-aside">
          <Tooltip title={formatAbsolute(when)}>
            <span className="scrape-popover-row-time">{formatRelative(when)}</span>
          </Tooltip>
          {job.state === "awaiting_2fa" ? (
            <button type="button" className="scrape-popover-2fa-btn" onClick={(e) => openTwoFA(job, e)}>
              Submit code
            </button>
          ) : cancellable ? (
            <Tooltip title="Cancel this run">
              <button
                type="button"
                className="scrape-popover-cancel-btn"
                onClick={(e) => handleCancel(job, e)}
                aria-label="Cancel"
              >
                <CloseOutlined />
              </button>
            </Tooltip>
          ) : isSuperAdmin ? (
            // Only on finished runs — the server refuses to hide one still in
            // flight, since its next WS event would put it straight back.
            <Tooltip title={isHidden(job) ? "Show this run again" : "Hide this run from the panel"}>
              <button
                type="button"
                className="agent-runs-hide-btn"
                onClick={(e) => handleHide(job, e)}
                aria-label={isHidden(job) ? "Unhide run" : "Hide run"}
              >
                {isHidden(job) ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={"agent-runs-trigger" + (open ? " is-open" : "")}
        onClick={() => setOpen(true)}
        aria-label="Agent runs"
      >
        <RobotOutlined />
        <span className="agent-runs-trigger-label">Agent Runs</span>
        {activeRows.length > 0 ? (
          <span className="agent-runs-trigger-badge">{activeRows.length > 99 ? "99+" : activeRows.length}</span>
        ) : null}
      </button>

      <Drawer
        title={
          <div className="agent-runs-title">
            <span>Agent Runs</span>
            <Tooltip title="Refresh">
              <button
                type="button"
                className="agent-runs-refresh"
                onClick={() => fetchRuns(debouncedSearch)}
                aria-label="Refresh"
              >
                <ReloadOutlined spin={loading} />
              </button>
            </Tooltip>
          </div>
        }
        placement="right"
        width={460}
        open={open}
        onClose={() => setOpen(false)}
        className="agent-runs-drawer"
        bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
      >
        <div className="agent-runs-toolbar">
          <Input
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by patient…"
            prefix={<SearchOutlined />}
            className="agent-runs-search"
          />
          <div className="agent-runs-filter" role="tablist" aria-label="Filter runs by state">
            {[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Done", value: "succeeded" },
              { label: "Failed", value: "failed" },
              // Where a hidden run goes to be found again — so hiding is never a
              // one-way door. Absent entirely for everyone else.
              ...(isSuperAdmin ? [{ label: "Hidden", value: "hidden" }] : []),
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={stateFilter === opt.value}
                className={"agent-runs-chip" + (stateFilter === opt.value ? " is-active" : "")}
                onClick={() => setStateFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="agent-runs-body">
          {loading && rows.length === 0 ? (
            <div className="agent-runs-loading">
              <Spin />
            </div>
          ) : rows.length === 0 ? (
            <div className="agent-runs-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  debouncedSearch
                    ? "No runs match that patient."
                    : showingHidden
                      ? "Nothing hidden. Runs you hide from the panel show up here."
                      : "No agent runs yet."
                }
              />
            </div>
          ) : (
            <>
              {activeRows.length > 0 ? (
                <div className="agent-runs-section">
                  <div className="agent-runs-section-header agent-runs-section-header-active">
                    <span>Active</span>
                    <span className="agent-runs-section-count">{activeRows.length}</span>
                  </div>
                  {activeRows.map(renderRow)}
                </div>
              ) : null}
              {pastRows.length > 0 ? (
                <div className="agent-runs-section">
                  <div className="agent-runs-section-header">
                    <span>History</span>
                    <span className="agent-runs-section-count">{pastRows.length}</span>
                  </div>
                  {pastRows.map(renderRow)}
                </div>
              ) : null}
            </>
          )}
        </div>
      </Drawer>

      {insuranceTarget ? (
        <TreatmentPlanListRowInsuranceActions
          key={insuranceTarget.patient.id}
          patient={insuranceTarget.patient}
          hideTriggers
          autoOpenInsuranceTab={insuranceTarget.tab}
          onInsuranceModalClosed={() => setInsuranceTarget(null)}
        />
      ) : null}
    </>
  );
}

const mapStateToProps = (state) => ({
  liveJobs: selectAllJobs(state),
});

const mapDispatchToProps = (dispatch) => ({
  upsertJob: (job) => dispatch({ type: SCRAPE_JOB_UPSERT, job }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentRunsLauncher);
